import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { DONATE_FAQ } from '../data/donateFaq';
import { EVENTS } from '../data/events';

const BASE_URL = 'https://fondationenat.org';

const META = {
  '/': {
    fr: {
      title: "Fondation Enat Espoir & Protection | Aide aux femmes et enfants victimes de violence au Québec",
      description: "Organisme de bienfaisance québécois qui soutient les femmes et enfants victimes de violence conjugale et familiale : hébergement, accompagnement, reconstruction. Faites un don, recevez un reçu fiscal.",
    },
    en: {
      title: "Fondation Enat Espoir & Protection | Helping Women and Children Victims of Violence in Quebec",
      description: "Quebec registered charity supporting women and children victims of domestic and family violence: shelter, support, rebuilding lives. Donate and receive a tax receipt.",
    },
  },
  '/about': {
    fr: {
      title: "À propos de notre mission | Fondation Enat Espoir & Protection",
      description: "Découvrez la mission, les valeurs et l'histoire de la Fondation Enat Espoir & Protection, organisme de bienfaisance enregistré au Québec dédié aux femmes et enfants victimes de violence.",
    },
    en: {
      title: "About Our Mission | Fondation Enat Espoir & Protection",
      description: "Learn about the mission, values and story of Fondation Enat Espoir & Protection, a registered Quebec charity dedicated to women and children victims of violence.",
    },
  },
  '/services': {
    fr: {
      title: "Nos services d'aide, d'hébergement et d'accompagnement | Fondation Enat",
      description: "Hébergement d'urgence, soutien psychosocial, accompagnement juridique et aide à la reconstruction pour les femmes et enfants victimes de violence au Québec.",
    },
    en: {
      title: "Our Support, Shelter and Assistance Services | Fondation Enat",
      description: "Emergency shelter, psychosocial support, legal assistance and rebuilding support for women and children victims of violence in Quebec.",
    },
  },
  '/volunteer': {
    fr: {
      title: "Devenir bénévole | Fondation Enat Espoir & Protection",
      description: "Donnez de votre temps pour aider les femmes et enfants victimes de violence au Québec. Rejoignez l'équipe de bénévoles de la Fondation Enat Espoir & Protection.",
    },
    en: {
      title: "Become a Volunteer | Fondation Enat Espoir & Protection",
      description: "Give your time to help women and children victims of violence in Quebec. Join the volunteer team of Fondation Enat Espoir & Protection.",
    },
  },
  '/partners': {
    fr: {
      title: "Devenir partenaire ou commanditaire | Fondation Enat Espoir & Protection",
      description: "Entreprises et organisations : devenez partenaire de la Fondation Enat Espoir & Protection et contribuez à protéger les femmes et enfants victimes de violence au Québec.",
    },
    en: {
      title: "Become a Partner or Sponsor | Fondation Enat Espoir & Protection",
      description: "Businesses and organizations: partner with Fondation Enat Espoir & Protection and help protect women and children victims of violence in Quebec.",
    },
  },
  '/donate': {
    fr: {
      title: "Faire un don — Reçu fiscal offert | Fondation Enat Espoir & Protection",
      description: "Faites un don unique ou mensuel par carte ou PayPal pour aider les femmes et enfants victimes de violence au Québec. Reçu fiscal officiel pour tout don de 25$ et plus.",
    },
    en: {
      title: "Donate — Tax Receipt Provided | Fondation Enat Espoir & Protection",
      description: "Make a one-time or monthly donation by card or PayPal to help women and children victims of violence in Quebec. Official tax receipt for donations of $25 and more.",
    },
  },
  '/events': {
    fr: {
      title: "Événements et activités-bénéfice | Fondation Enat Espoir & Protection",
      description: "Découvrez nos événements-bénéfice au profit des femmes et enfants victimes de violence au Québec : Garden Party, conférences, collectes de fonds. Achetez vos billets en ligne.",
    },
    en: {
      title: "Events and Fundraisers | Fondation Enat Espoir & Protection",
      description: "Discover our fundraising events benefiting women and children victims of violence in Quebec: Garden Party, conferences, fundraisers. Buy your tickets online.",
    },
  },
  '/shop': {
    fr: {
      title: "Boutique solidaire | Fondation Enat Espoir & Protection",
      description: "Achetez nos articles solidaires (t-shirts, tasses, sacs, casquettes) et soutenez les femmes et enfants victimes de violence au Québec. Paiement sécurisé en ligne.",
    },
    en: {
      title: "Charity Shop | Fondation Enat Espoir & Protection",
      description: "Buy our charity merchandise (t-shirts, mugs, tote bags, caps) and support women and children victims of violence in Quebec. Secure online payment.",
    },
  },
  '/contact': {
    fr: {
      title: "Contactez-nous | Fondation Enat Espoir & Protection",
      description: "Besoin d'aide ou d'information ? Contactez la Fondation Enat Espoir & Protection à Sainte-Julie, Québec. Nous sommes là pour les femmes et enfants victimes de violence.",
    },
    en: {
      title: "Contact Us | Fondation Enat Espoir & Protection",
      description: "Need help or information? Contact Fondation Enat Espoir & Protection in Sainte-Julie, Quebec. We are here for women and children victims of violence.",
    },
  },
  '/wall-of-honor': {
    fr: {
      title: "Mur d'honneur de nos donateurs | Fondation Enat Espoir & Protection",
      description: "Merci à nos généreux donateurs qui redonnent espoir aux femmes et enfants victimes de violence au Québec. Découvrez le mur d'honneur de la Fondation Enat.",
    },
    en: {
      title: "Donor Wall of Honor | Fondation Enat Espoir & Protection",
      description: "Thank you to our generous donors who bring hope back to women and children victims of violence in Quebec. Discover the Fondation Enat wall of honor.",
    },
  },
};

const NOINDEX_PREFIXES = ['/admin', '/donor'];

const setMeta = (attr, key, content) => {
  let el = document.head.querySelector(`meta[${attr}="${key}"]`);
  if (!el) {
    el = document.createElement('meta');
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.setAttribute('content', content);
};

export const SeoManager = () => {
  const { pathname } = useLocation();
  const { language } = useLanguage();

  useEffect(() => {
    const lang = language === 'en' ? 'en' : 'fr';
    const noindex = NOINDEX_PREFIXES.some((p) => pathname.startsWith(p));
    const meta = (META[pathname] || META['/'])[lang];

    document.title = meta.title;
    document.documentElement.lang = lang;
    setMeta('name', 'description', meta.description);
    setMeta('name', 'robots', noindex ? 'noindex, nofollow' : 'index, follow');
    setMeta('property', 'og:title', meta.title);
    setMeta('property', 'og:description', meta.description);
    setMeta('property', 'og:url', BASE_URL + pathname);
    setMeta('property', 'og:locale', lang === 'en' ? 'en_CA' : 'fr_CA');
    setMeta('name', 'twitter:title', meta.title);
    setMeta('name', 'twitter:description', meta.description);

    let canonical = document.head.querySelector('link[rel="canonical"]');
    if (!canonical) {
      canonical = document.createElement('link');
      canonical.setAttribute('rel', 'canonical');
      document.head.appendChild(canonical);
    }
    canonical.setAttribute('href', BASE_URL + (pathname === '/' ? '/' : pathname));

    const FAQ_ID = 'seo-faq-jsonld';
    let faqScript = document.getElementById(FAQ_ID);
    if (pathname === '/donate') {
      const data = {
        "@context": "https://schema.org",
        "@type": "FAQPage",
        "mainEntity": DONATE_FAQ.map((f) => ({
          "@type": "Question",
          "name": f.q[lang],
          "acceptedAnswer": { "@type": "Answer", "text": f.a[lang] },
        })),
      };
      if (!faqScript) {
        faqScript = document.createElement('script');
        faqScript.type = 'application/ld+json';
        faqScript.id = FAQ_ID;
        document.head.appendChild(faqScript);
      }
      faqScript.textContent = JSON.stringify(data);
    } else if (faqScript) {
      faqScript.remove();
    }

    const EVENT_ID = 'seo-event-jsonld';
    let eventScript = document.getElementById(EVENT_ID);
    const upcomingEvents = EVENTS.filter((e) => new Date(e.end) >= new Date());
    if (pathname === '/events' && upcomingEvents.length > 0) {
      const data = upcomingEvents.map((e) => ({
        "@context": "https://schema.org",
        "@type": "Event",
        "name": e.title[lang],
        "description": e.description[lang],
        "startDate": e.start,
        "endDate": e.end,
        "eventStatus": "https://schema.org/EventScheduled",
        "eventAttendanceMode": "https://schema.org/OfflineEventAttendanceMode",
        "image": [e.image],
        "location": {
          "@type": "Place",
          "name": e.location,
          "address": {
            "@type": "PostalAddress",
            "streetAddress": "9475 Rue Meilleur",
            "addressLocality": "Montréal",
            "addressRegion": "QC",
            "addressCountry": "CA",
          },
        },
        "offers": {
          "@type": "Offer",
          "price": "80",
          "priceCurrency": "CAD",
          "url": e.ticketUrl,
          "availability": "https://schema.org/InStock",
        },
        "organizer": {
          "@type": "Organization",
          "name": "Fondation Enat Espoir & Protection",
          "url": BASE_URL,
        },
      }));
      if (!eventScript) {
        eventScript = document.createElement('script');
        eventScript.type = 'application/ld+json';
        eventScript.id = EVENT_ID;
        document.head.appendChild(eventScript);
      }
      eventScript.textContent = JSON.stringify(data);
    } else if (eventScript) {
      eventScript.remove();
    }
  }, [pathname, language]);

  return null;
};

export default SeoManager;
